import {useTranslation} from "react-i18next";

import {type RevaLanguage, selectByLanguage} from "@reva/shared/i18n";

type Translation = ReturnType<typeof useTranslation>;

/**
 * Narrows the language i18next reports to the languages REVA supports.
 *
 * i18next types `language` as `string`, but it only ever hands out a code it resolved against
 * `supportedLngs` (see `initializeI18n` in `src/i18n.ts`, and `initTestI18n` for the tests): a
 * region variant like "de-DE" is folded to "de", anything unknown to the fallback "en". The
 * narrowing belongs here, in the place that reads the raw value, instead of at every call site.
 */
const toRevaLanguage = (language: string): RevaLanguage => language as RevaLanguage;

/**
 * React hook to select the current i18n language as RevaLanguage.
 */
export function useLanguage(): RevaLanguage {
    const {i18n} = useTranslation();
    return toRevaLanguage(i18n.language);
}

/** What {@link useRevaTranslation} hands out. */
export interface RevaTranslation {
    /** translation function, as from `useTranslation()` */
    t: Translation["t"];
    /** the i18next instance, as from `useTranslation()` — for `changeLanguage`, `exists`, … */
    i18n: Translation["i18n"];
    /** the current UI language, already narrowed to the languages REVA supports */
    language: RevaLanguage;
}

/**
 * React hook to use instead of `useTranslation()` wherever the current language is needed next to
 * the translation function: `const {t, language} = useRevaTranslation()` saves the detour over
 * `i18n.language`, which i18next types as a plain `string`.
 */
export function useRevaTranslation(): RevaTranslation {
    const {t, i18n} = useTranslation();
    return {t, i18n, language: toRevaLanguage(i18n.language)};
}

/**
 * Language resolution function for the currently selected language.
 */
export type SelectByCurrentLanguage = (textDe: string, textEn: string) => string;

/**
 * React Hook to get a SelectByCurrentLanguage function for the currently set language.
 * @return {SelectByCurrentLanguage} translation function
 */
export function useSelectByCurrentLanguage(): SelectByCurrentLanguage {
    const {i18n} = useTranslation();
    return (textDe: string, textEn: string): string => selectByLanguage(toRevaLanguage(i18n.language), textDe, textEn);
}
